import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { adminSubscriptionApi } from '../api/adminSubscription.api';
import { adminPlanPriceApi } from '../api/adminSubscriptionPlan.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';

function StatusBadge({ status }) {
  const normalized = (status || '').toLowerCase();
  let bg = '#F1F5F9';
  let color = '#475569';
  let border = '#E2E8F0';

  if (normalized === 'active') {
    bg = '#F0FDF4';
    color = '#15803D';
    border = '#b7e5bb';
  } else if (normalized === 'trialing' || normalized === 'trial') {
    bg = '#EFF6FF';
    color = '#1D4ED8';
    border = '#BFDBFE';
  } else if (normalized === 'past_due' || normalized === 'unpaid') {
    bg = '#FFFBEB';
    color = '#B45309';
    border = '#FDE68A';
  } else if (normalized === 'canceled' || normalized === 'cancelled' || normalized === 'inactive') {
    bg = '#FEF2F2';
    color = '#B91C1C';
    border = '#FECACA';
  } else if (normalized === 'paused') {
    bg = '#F5F3FF';
    color = '#6D28D9';
    border = '#DDD6FE';
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  );
}

export default function SubscriptionDetail() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Plan actions state
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [immediateChange, setImmediateChange] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    page,
    limit,
    total,
    loading: txLoading,
    setLoading: setTxLoading,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(5);

  // Load plans catalog for admin action modal
  useEffect(() => {
    const fetchPlans = async () => {
      const result = await apiHandler(() => adminPlanPriceApi.list());
      if (result.success) {
        const responseData = unwrapData(result);
        const planList = responseData.plans || responseData || [];
        setPlans(planList.filter((p) => p.isActive));
      }
    };
    fetchPlans();
  }, []);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await apiHandler(() => adminSubscriptionApi.getById(subscriptionId));
    if (result.success) {
      setData(unwrapData(result));
    } else {
      setError(result.message || 'Failed to load subscription details');
    }
    setLoading(false);
  }, [subscriptionId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleCancel = async (immediate = false) => {
    if (!window.confirm(`Are you sure you want to cancel this subscription ${immediate ? 'IMMEDIATELY' : 'at the end of the billing cycle'}?`)) {
      return;
    }
    setActionLoading(true);
    const result = await apiHandler(() => adminSubscriptionApi.cancel(subscriptionId, immediate));
    if (result.success) {
      alert('Subscription canceled successfully.');
      fetchDetail();
    } else {
      alert(result.message || 'Failed to cancel subscription.');
    }
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    if (!window.confirm('Are you sure you want to reactivate this canceled subscription?')) {
      return;
    }
    setActionLoading(true);
    const result = await apiHandler(() => adminSubscriptionApi.reactivate(subscriptionId));
    if (result.success) {
      alert('Subscription reactivated successfully.');
      fetchDetail();
    } else {
      alert(result.message || 'Failed to reactivate subscription.');
    }
    setActionLoading(false);
  };

  const handleChangePlan = async () => {
    if (!selectedPlanId) {
      alert('Please select a new plan.');
      return;
    }
    const chosenPlan = plans.find((p) => p.id === selectedPlanId);
    if (!chosenPlan) return;

    if (!window.confirm(`Are you sure you want to change the user's plan to "${chosenPlan.code}"?`)) {
      return;
    }

    setActionLoading(true);
    const result = await apiHandler(() => adminSubscriptionApi.changePlan(subscriptionId, selectedPlanId, immediateChange));
    if (result.success) {
      alert('Subscription plan updated successfully.');
      setShowPlanModal(false);
      fetchDetail();
    } else {
      alert(result.message || 'Failed to change plan.');
    }
    setActionLoading(false);
  };

  const fetchTransactions = useCallback(async (pageNum = page) => {
    setTxLoading(true);
    const result = await apiHandler(() =>
      adminSubscriptionApi.getTransactions(subscriptionId, { page: pageNum, limit })
    );
    if (result.success) {
      const responseData = unwrapData(result);
      setTransactions(responseData.transactions || []);
      applyPaginationMeta(responseData);
    }
    setTxLoading(false);
  }, [subscriptionId, page, limit, applyPaginationMeta, setTxLoading]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions(page);
    }
  }, [activeTab, page, fetchTransactions]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 12 }} />
        <div>Loading subscription details…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 20 }}>
        <PageHeader
          title="Subscription Details"
          trail={[{ label: 'Payment Management' }, { label: 'User Subscription', to: '/subscriptions' }, { label: 'Error' }]}
        />
        <div style={{ padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, color: '#B91C1C', fontSize: 13, marginTop: 16 }}>
          {error || 'Subscription not found'}
        </div>
        <button type="button" className="sp-btn sp-btn-secondary" style={{ marginTop: 16 }} onClick={() => navigate('/subscriptions')}>
          Back to List
        </button>
      </div>
    );
  }

  const { subscription, limits, paymentMethods } = data;
  const user = subscription.user || {};
  const plan = subscription.plan || {};
  const planTier = plan.planTier || {};
  const isAdminGranted = Boolean(subscription.adminGranted);

  return (
    <>
      <PageHeader
        title={`Subscription: ${user.firstName || ''} ${user.lastName || ''}`}
        subtitle={`ID: ${subscription.id}`}
        icon="fa-user-tag"
        trail={[{ label: 'Payment Management' }, { label: 'User Subscription', to: '/subscriptions' }, { label: 'Detail' }]}
        actions={
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/subscriptions')}>
            <i className="fa-solid fa-arrow-left" /> Back to list
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start fade-up">
        {/* Left Column: Quick Profile Card */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          <div className="section-card" style={{ textAlign: 'center', padding: '28px 20px', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#1E293B,#0F172A)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                fontWeight: 700,
                color: '#fff',
                margin: '0 auto 16px',
                border: '3px solid #E2E8F0',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            >
              {((user.firstName || 'U')[0] + (user.lastName || 'P')[0]).toUpperCase()}
            </div>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#0F172A' }}>
              {user.firstName} {user.lastName}
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: '#64748B', wordBreak: 'break-all' }}>{user.email}</p>

            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 20, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', marginBottom: 4 }}>Status</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <StatusBadge status={subscription.status} />
                  {isAdminGranted && <StatusBadge status="Admin granted" />}
                </div>
              </div>
              <div>
                <span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', marginBottom: 4 }}>Plan Code</span>
                <code style={{ fontSize: 12, color: '#0F172A', fontWeight: 700, background: '#F1F5F9', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                  {plan.code || '—'}
                </code>
              </div>
              <div>
                <span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', marginBottom: 4 }}>Mobile Number</span>
                <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>{user.mobile || '—'}</span>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 20, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
              <span style={{ display: 'block', fontSize: 10, textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', marginBottom: 2 }}>Administrative Controls</span>

              {isAdminGranted && (
                <p style={{ margin: 0, fontSize: 11, color: '#B45309', lineHeight: 1.4 }}>
                  Admin-granted membership. Prefer Revoke grant on the user profile. Change Plan / Reactivate are disabled.
                </p>
              )}

              {subscription.status !== 'canceled' ? (
                <>
                  <button
                    type="button"
                    className="sp-btn sp-btn-secondary sp-btn-sm"
                    onClick={() => {
                      setSelectedPlanId(plan.id || '');
                      setShowPlanModal(true);
                    }}
                    disabled={actionLoading || isAdminGranted}
                    style={{ width: '100%', justifyContent: 'center', height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <i className="fa-solid fa-arrows-rotate" /> Change Plan
                  </button>

                  {subscription.cancelAtPeriodEnd ? (
                    <button
                      type="button"
                      className="sp-btn sp-btn-success sp-btn-sm"
                      onClick={handleReactivate}
                      disabled={actionLoading || isAdminGranted}
                      style={{ width: '100%', justifyContent: 'center', height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#10B981', color: '#fff', border: '1px solid #10B981' }}
                    >
                      <i className="fa-solid fa-rotate-left" /> Reactivate Plan
                    </button>
                  ) : (
                    <>
                      {!isAdminGranted && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-danger sp-btn-sm"
                          onClick={() => handleCancel(false)}
                          disabled={actionLoading}
                          style={{ width: '100%', justifyContent: 'center', height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' }}
                        >
                          <i className="fa-solid fa-ban" /> Cancel at Period End
                        </button>
                      )}
                      <button
                        type="button"
                        className="sp-btn sp-btn-danger sp-btn-sm"
                        onClick={() => handleCancel(true)}
                        disabled={actionLoading}
                        style={{ width: '100%', justifyContent: 'center', height: '38px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }}
                      >
                        <i className="fa-solid fa-trash-can" /> Cancel Immediately
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11, color: '#64748B', fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                  No actions available for canceled subscriptions.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Information Tabs */}
        <div className="lg:col-span-3">
          {/* Tab Navigation Headers */}
          <div className="od-tabs">
            {[
              { id: 'overview', label: 'Overview & Billing', icon: 'fa-chart-pie' },
              { id: 'limits', label: 'Plan Limits & Services', icon: 'fa-shield-halved' },
              { id: 'payment', label: 'Saved Payment Cards', icon: 'fa-credit-card' },
              { id: 'transactions', label: 'Transaction History', icon: 'fa-receipt' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`od-tab ${activeTab === tab.id ? 'od-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content Panels */}
          <div className="section-card" style={{ padding: '24px' }}>
            {activeTab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#1E293B' }}>Subscription Billing Overview</h4>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  {/* Stripe Subscription ID */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#EFF6FF', color: '#1D4ED8', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-brands fa-stripe" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Stripe Subscription ID</span>
                      <span style={{ fontSize: 13, fontFamily: "'DM Mono',monospace", color: '#0F172A', wordBreak: 'break-all' }}>
                        {subscription.stripeSubscriptionId || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Stripe Customer ID */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#F0FDF4', color: '#15803D', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-solid fa-address-card" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Stripe Customer ID</span>
                      <span style={{ fontSize: 13, fontFamily: "'DM Mono',monospace", color: '#0F172A', wordBreak: 'break-all' }}>
                        {subscription.stripeCustomerId || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Current Period Start */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#FFF7ED', color: '#C2410C', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-regular fa-calendar-plus" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Current Period Start</span>
                      <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>
                        {subscription.currentPeriodStart ? new Date(subscription.currentPeriodStart).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Next Billing At */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#F5F3FF', color: '#6D28D9', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-regular fa-calendar-check" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Next Billing At</span>
                      <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>
                        {subscription.nextBillingAt ? new Date(subscription.nextBillingAt).toLocaleString() : 'Open Period / Auto-renew off'}
                      </span>
                    </div>
                  </div>

                  {/* Last Payment At */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#FEF2F2', color: '#B91C1C', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-solid fa-clock-rotate-left" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Last Payment At</span>
                      <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600 }}>
                        {subscription.lastPaymentAt ? new Date(subscription.lastPaymentAt).toLocaleString() : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Billing Cycle */}
                  <div style={{ padding: '16px 20px', background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ background: '#EFF6FF', color: '#1D4ED8', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      <i className="fa-solid fa-arrows-spin" />
                    </div>
                    <div>
                      <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Billing Cycle</span>
                      <span style={{ fontSize: 13, color: '#0F172A', fontWeight: 600, textTransform: 'capitalize' }}>
                        {subscription.billingCycle || 'month'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 20 }}>
                  <h5 style={{ margin: '0 0 16px', fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Subscription Timeline</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative', paddingLeft: 20 }}>
                    {/* Timeline Line */}
                    <div style={{ position: 'absolute', left: 4, top: 8, bottom: 8, width: 2, background: '#E2E8F0' }} />

                    {/* Created Date */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3B82F6', border: '2px solid #fff', boxShadow: '0 0 0 2px #3B82F6', marginTop: 4, zIndex: 1, left: -20, position: 'absolute' }} />
                      <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#64748B', fontWeight: 500 }}>Created Date</span>
                        <span style={{ color: '#0F172A', fontWeight: 700 }}>
                          {data.timeline?.createdAt ? new Date(data.timeline.createdAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Last Payment Date */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981', border: '2px solid #fff', boxShadow: '0 0 0 2px #10B981', marginTop: 4, zIndex: 1, left: -20, position: 'absolute' }} />
                      <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#64748B', fontWeight: 500 }}>Last Payment Date</span>
                        <span style={{ color: '#0F172A', fontWeight: 700 }}>
                          {data.timeline?.lastPaymentAt ? new Date(data.timeline.lastPaymentAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Next Billing Date */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#8B5CF6', border: '2px solid #fff', boxShadow: '0 0 0 2px #8B5CF6', marginTop: 4, zIndex: 1, left: -20, position: 'absolute' }} />
                      <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#64748B', fontWeight: 500 }}>Next Billing Date</span>
                        <span style={{ color: '#0F172A', fontWeight: 700 }}>
                          {data.timeline?.nextBillingAt ? new Date(data.timeline.nextBillingAt).toLocaleString() : 'Open Period / Auto-renew off'}
                        </span>
                      </div>
                    </div>

                    {data.timeline?.cancellationRequestedAt && (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: '2px solid #fff', boxShadow: '0 0 0 2px #F59E0B', marginTop: 4, zIndex: 1, left: -20, position: 'absolute' }} />
                        <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontSize: 13 }}>
                          <span style={{ color: '#B45309', fontWeight: 500 }}>Cancellation Requested</span>
                          <span style={{ color: '#B45309', fontWeight: 700 }}>
                            {new Date(data.timeline.cancellationRequestedAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {data.timeline?.cancellationEffectiveAt && (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative' }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', border: '2px solid #fff', boxShadow: '0 0 0 2px #EF4444', marginTop: 4, zIndex: 1, left: -20, position: 'absolute' }} />
                        <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', fontSize: 13 }}>
                          <span style={{ color: '#B91C1C', fontWeight: 500 }}>Cancellation Effective</span>
                          <span style={{ color: '#B91C1C', fontWeight: 700 }}>
                            {new Date(data.timeline.cancellationEffectiveAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cancel behavior note */}
                {/* <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 20 }}>
                  <h5 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Cancel Period End Behavior</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: subscription.cancelAtPeriodEnd ? '#EF4444' : '#10B981',
                        display: 'inline-block'
                      }}
                    />
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                      {subscription.cancelAtPeriodEnd
                        ? 'Will automatically cancel at current subscription period end.'
                        : 'Will automatically renew at the next billing interval.'}
                    </span>
                  </div>
                </div> */}
              </div>
            )}

            {activeTab === 'limits' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#1E293B' }}>Plan Limits & Dynamic Usage</h4>
                <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748B' }}>
                  Assigned capacity vs actual real-time usage for <strong>{planTier.name || 'Plan Tier'}</strong>
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  {/* Contacts */}
                  <div style={{ padding: 16, background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                    <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>Trusted Contacts</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{data.usage?.contacts?.used ?? 0}</span>
                      <span style={{ fontSize: 12, color: '#64748B' }}>/ {data.usage?.contacts?.allowed ?? 0} Used</span>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        background: '#ec7426',
                        width: `${Math.min(100, ((data.usage?.contacts?.used ?? 0) / (data.usage?.contacts?.allowed || 1)) * 100)}%`
                      }} />
                    </div>
                  </div>

                  {/* Alerts */}
                  <div style={{ padding: 16, background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                    <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>Alerts Sent</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{data.usage?.alerts?.used ?? 0}</span>
                      <span style={{ fontSize: 12, color: '#64748B' }}>/ {data.usage?.alerts?.allowed ?? 0} Sent</span>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        background: '#3B82F6',
                        width: `${Math.min(100, ((data.usage?.alerts?.used ?? 0) / (data.usage?.alerts?.allowed || 1)) * 100)}%`
                      }} />
                    </div>
                  </div>

                  {/* Family / Group Members */}
                  <div style={{ padding: 16, background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                    <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>Group Members</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{data.usage?.trustedCircles?.used ?? 0}</span>
                      <span style={{ fontSize: 12, color: '#64748B' }}>/ {data.usage?.trustedCircles?.allowed ?? 0} Added</span>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        background: '#10B981',
                        width: `${Math.min(100, ((data.usage?.trustedCircles?.used ?? 0) / (data.usage?.trustedCircles?.allowed || 1)) * 100)}%`
                      }} />
                    </div>
                  </div>

                  {/* Organizations */}
                  <div style={{ padding: 16, background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9' }}>
                    <span style={{ display: 'block', fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase' }}>Organizations Joined</span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{data.usage?.organizations?.used ?? 0}</span>
                      <span style={{ fontSize: 12, color: '#64748B' }}>/ {data.usage?.organizations?.allowed ?? 0} Limit</span>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        background: '#8B5CF6',
                        width: `${Math.min(100, ((data.usage?.organizations?.used ?? 0) / (data.usage?.organizations?.allowed || 1)) * 100)}%`
                      }} />
                    </div>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 16 }}>
                  <h5 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#475569' }}>Premium Features Availability</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i
                        className={`fas ${limits?.customAlerts ? 'fa-circle-check text-green-600' : 'fa-circle-xmark text-slate-300'}`}
                        style={{ color: limits?.customAlerts ? '#16A34A' : '#94A3B8', fontSize: 16 }}
                      />
                      <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>Custom Alert Templates</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <i
                        className={`fas ${limits?.prioritySupport ? 'fa-circle-check text-green-600' : 'fa-circle-xmark text-slate-300'}`}
                        style={{ color: limits?.prioritySupport ? '#16A34A' : '#94A3B8', fontSize: 16 }}
                      />
                      <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>Priority Support Queue</span>
                    </div>
                  </div>
                </div>

                {planTier.includeNotes && planTier.includeNotes.length > 0 && (
                  <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 16 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Included in this Plan</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                      {planTier.includeNotes.map((note) => (
                        <div key={note.key || note.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i
                            className={`fas ${note.included ? 'fa-circle-check text-green-600' : 'fa-circle-xmark text-slate-300'}`}
                            style={{ color: note.included ? '#16A34A' : '#94A3B8', fontSize: 16 }}
                          />
                          <span style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>{note.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'payment' && (
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1E293B' }}>Saved Payment Methods</h4>

                {(!paymentMethods || paymentMethods.length === 0) ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#64748B', fontSize: 12, border: '1px dashed #E2E8F0', borderRadius: 8 }}>
                    No payment cards stored for this user.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {paymentMethods.map((pm) => (
                      <div
                        key={pm.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: '#fff',
                          border: '1px solid #E2E8F0',
                          borderRadius: 10,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ background: '#F8FAFC', padding: '6px 10px', borderRadius: 6, border: '1px solid #E2E8F0' }}>
                            <i className="fa-solid fa-credit-card" style={{ color: '#475569', fontSize: 16 }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', textTransform: 'capitalize' }}>
                              {pm.cardBrand || 'Card'} •••• {pm.cardLast4 || '0000'}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748B' }}>
                              Expires: {pm.expiryMonth}/{pm.expiryYear}
                            </div>
                          </div>
                        </div>

                        {pm.isDefault && (
                          <span style={{ background: '#F0FDF4', color: '#16A34A', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid #b7e5bb' }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div>
                <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1E293B' }}>Billing Invoice & Payment History</h4>

                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ minWidth: 600 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Stripe Invoice</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txLoading ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#64748B' }}>
                            Loading transactions list…
                          </td>
                        </tr>
                      ) : transactions.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: 20, color: '#64748B' }}>
                            No invoice records found.
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx) => (
                          <tr key={tx.id}>
                            <td style={{ fontSize: 12 }}>
                              {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                            <td style={{ fontSize: 12, textTransform: 'capitalize', fontWeight: 600, color: '#334155' }}>
                              {tx.type || 'subscription'}
                            </td>
                            <td style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#64748B' }}>
                              {tx.stripeInvoiceId ? `${tx.stripeInvoiceId.slice(0, 15)}...` : '—'}
                            </td>
                            <td style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
                              {tx.amount ? `$${tx.amount} ${tx.currency || 'USD'}` : '—'}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  borderRadius: 8,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  background: tx.status === 'succeeded' || tx.status === 'paid' ? '#F0FDF4' : '#FEF2F2',
                                  color: tx.status === 'succeeded' || tx.status === 'paid' ? '#16A34A' : '#EF4444',
                                }}
                              >
                                {tx.status}
                              </span>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {tx.invoicePdfUrl ? (
                                <a
                                  href={tx.invoicePdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="sp-btn sp-btn-ghost sp-btn-sm"
                                  style={{ textDecoration: 'none' }}
                                >
                                  <i className="fa-solid fa-file-pdf" style={{ marginRight: 4 }} /> PDF
                                </a>
                              ) : (
                                <span style={{ color: '#94A3B8', fontSize: 11 }}>N/A</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination {...paginationProps} />
              </div>
            )}
          </div>
        </div>
      </div>
      {showPlanModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div className="section-card fade-up" style={{ width: '100%', maxWidth: 450, padding: 24, borderRadius: 12, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Change Subscription Plan</h3>

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="plan-select" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Select New Plan</label>
              <select
                id="plan-select"
                className="cm-filter-select"
                style={{ width: '100%', height: 40, padding: '0 12px' }}
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
              >
                <option value="">-- Choose Plan --</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.code} (${p.price}/{p.interval})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="immediate-check"
                type="checkbox"
                checked={immediateChange}
                onChange={(e) => setImmediateChange(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <label htmlFor="immediate-check" style={{ fontSize: 12, fontWeight: 500, color: '#475569', cursor: 'pointer' }}>
                Apply immediately (creates Stripe proration charge)
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'end', gap: 10 }}>
              <button
                type="button"
                className="sp-btn sp-btn-secondary"
                onClick={() => setShowPlanModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={handleChangePlan}
                disabled={actionLoading || !selectedPlanId}
                style={{ background: '#ec7426', color: '#fff', border: '1px solid #ec7426' }}
              >
                {actionLoading ? 'Updating...' : 'Update Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
